// src/components/block/broadcastingChannelManagment/BroadcastingChannelCharacteristics_ThirdPartyStreamingSystemV2.tsx

import React, { useEffect, useState, useCallback } from 'react';
import classNames from 'classnames';
import axios from 'axios';
import styles from './BroadcastingChannelCharacteristics_ThirdPartyStreamingSystemV2.module.scss';
import AnimatedWaitingIcon2 from '../../widget/animations/animatedWaitingIcon2';
import Icon           from '../../widget/images/Icon';

import {
  getThirdPartyStreamingServices,
  createThirdPartyStreamingChannel,
  deleteThirdPartyStreamingChannel,
  switchThirdPartyStreamingChannelStatus,
} from '../../../backendServices/broadcastingManagment/api_thirdPartyStreamingSystem';

import {
  ThirdPartyStreamingService,
  ThirdPartyStreamingChannel,
  initializedThirdPartyStreamingChannel,
} from '../../../dataTypes/broadcastingManagment/types_thirdPartyStreamingSystem';

import { API_URLS } from '../../../backendServices/urls';

// ── PATCH helper ─────────────────────────────────────────────────────────────
const patchChannel = async (ch: ThirdPartyStreamingChannel) => {
  const url = (API_URLS.patchThirdPartyStreamingChannel ?? '/api/third_party_streaming_channels/{id}')
    .replace('{id}', `${ch.id}`);
  try {
    const r = await axios.patch(
      url,
      { name: ch.name, streamUrl: ch.streamUrl, ingestionKey: ch.ingestionKey, description: ch.description, backupUrl: ch.backupUrl },
      { headers: { 'Content-Type': 'application/merge-patch+json' } },
    );
    return r.status === 200 ? { success: true } : { success: false, error: `HTTP ${r.status}` };
  } catch (e: any) { return { success: false, error: e?.message ?? 'Erreur inconnue' }; }
};

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const Ico = {
  Book:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  Dots:    () => <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>,
  Plus:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Play:    () => <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  Edit:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Trash:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  Eye:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  EyeOff:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>,
  Help:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  Lock:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  Check:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="12" height="12"><polyline points="20 6 9 17 4 12"/></svg>,
  ExternalLink: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,

  // ── Stat 1 : chaîne de lien (plateformes connectées) ──────────────────────
  StatConnected: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
  ),

  // ── Stat 2 : antenne broadcast avec ondes concentriques (plateforme active)
  StatActive: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22">
      {/* Point central */}
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>
      {/* Onde 1 — petite */}
      <path d="M8.5 8.5a5 5 0 0 1 7 7" strokeLinecap="round"/>
      <path d="M15.5 8.5a5 5 0 0 0-7 7" strokeLinecap="round"/>
      {/* Onde 2 — grande */}
      <path d="M5.6 5.6a9 9 0 0 1 12.8 12.8" strokeLinecap="round"/>
      <path d="M18.4 5.6a9 9 0 0 0-12.8 12.8" strokeLinecap="round"/>
    </svg>
  ),

  // ── Stat 3 : coche (tout va bien) — utilisée DANS le cercle vert ──────────
  StatCheck: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="18" height="18">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
};

// ── Platform visual config ────────────────────────────────────────────────────
const PLATFORM_VISUAL: Record<string, { bg: string; color: string; initial: string }> = {
  YouTube      : { bg: '#ff0000', color: '#fff', initial: 'YT' },
  Youtube      : { bg: '#ff0000', color: '#fff', initial: 'YT' },
  Facebook     : { bg: '#1877f2', color: '#fff', initial: 'FB' },
  Twitch       : { bg: '#9146ff', color: '#fff', initial: 'TW' },
  LinkedIn     : { bg: '#0a66c2', color: '#fff', initial: 'LI' },
  TikTok       : { bg: '#010101', color: '#fff', initial: 'TK' },
  Tiktok       : { bg: '#010101', color: '#fff', initial: 'TK' },
  Instagram    : { bg: '#e1306c', color: '#fff', initial: 'IG' },
  Twitter      : { bg: '#1da1f2', color: '#fff', initial: 'X'  },
  X            : { bg: '#111',    color: '#fff', initial: 'X'  },
  Dailymotion  : { bg: '#0066dc', color: '#fff', initial: 'DM' },
  Vimeo        : { bg: '#1ab7ea', color: '#fff', initial: 'VI' },
};

const PLATFORM_LOGO_SLUG: Record<string, string> = {
  YouTube     : 'youtube',
  Youtube     : 'youtube',
  Facebook    : 'facebook',
  Twitch      : 'twitch',
  LinkedIn    : 'linkedin',
  TikTok      : 'tiktok',
  Tiktok      : 'tiktok',
  Instagram   : 'instagram',
  Twitter     : 'x',
  X           : 'x',
  Dailymotion : 'dailymotion',
  Vimeo       : 'vimeo',
};

function getPlatformVisual(name: string) {
  return PLATFORM_VISUAL[name] ?? { bg: '#3a7fe8', color: '#fff', initial: name.slice(0, 2).toUpperCase() };
}

const PlatformLogo = ({ name, size = 22 }: { name: string; size?: number }) => {
  const slug = PLATFORM_LOGO_SLUG[name];
  if (slug) {
    return (
      <img
        src={`https://cdn.simpleicons.org/${slug}/ffffff`}
        width={size}
        height={size}
        alt={name}
        style={{ objectFit: 'contain', display: 'block' }}
      />
    );
  }
  const vis = getPlatformVisual(name);
  return (
    <span style={{ fontSize: size * 0.45, fontWeight: 700, color: vis.color }}>
      {vis.initial}
    </span>
  );
};

const CustomStreamIcon = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" width={size} height={size}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);

// ── Props ─────────────────────────────────────────────────────────────────────
export interface BroadcastingChannelCharacteristics_ThirdPartyStreamingSystemV2Props {
  broadcastingChannelId?             : number;
  broadcastingChannelName?           : string;
  thirdPartyStreamingChannelsToEdit? : ThirdPartyStreamingChannel[];
  onChange?                          : (newChannels: ThirdPartyStreamingChannel[]) => void;
  // « Configurer YouTube » du tableau de bord : compteur qui change à chaque demande
  focusYoutube?                      : number;
}

type ToastState = { msg: string; kind: 'success' | 'error' } | null;

export const BroadcastingChannelCharacteristics_ThirdPartyStreamingSystemV2 = ({
  broadcastingChannelId   = 0,
  broadcastingChannelName = '',
  thirdPartyStreamingChannelsToEdit,
  onChange,
  focusYoutube,
}: BroadcastingChannelCharacteristics_ThirdPartyStreamingSystemV2Props) => {

  const [services,     setServices]     = useState<ThirdPartyStreamingService[]>([]);
  const [channels,     setChannels]     = useState<ThirdPartyStreamingChannel[]>(thirdPartyStreamingChannelsToEdit ?? []);
  const [showWaiting,  setShowWaiting]  = useState(false);
  const [toast,        setToast]        = useState<ToastState>(null);
  const [confirmDel,   setConfirmDel]   = useState<number | null>(null);
  const [editIndex,    setEditIndex]    = useState<number | null>(null);
  const [editValues,   setEditValues]   = useState<Record<number, Partial<ThirdPartyStreamingChannel>>>({});
  const [showKey,      setShowKey]      = useState(false);
  const [showEditKey,  setShowEditKey]  = useState<Record<number, boolean>>({});

  const [newServiceId,    setNewServiceId]    = useState(-1);
  const [newName,         setNewName]         = useState('');
  const [newStreamUrl,    setNewStreamUrl]    = useState('');
  const [newIngestionKey, setNewIngestionKey] = useState('');

  const showToast = useCallback((msg: string, kind: 'success' | 'error') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    getThirdPartyStreamingServices().then(r => {
      if (r.code === 1 && r.payload.length > 0) {
        setServices(r.payload);
        prefillForm(r.payload[0]);
      }
    });
  }, []);

  useEffect(() => {
    const incoming = thirdPartyStreamingChannelsToEdit ?? [];
    setChannels(incoming);
    const init: Record<number, Partial<ThirdPartyStreamingChannel>> = {};
    incoming.forEach((ch, i) => { init[i] = { name: ch.name, streamUrl: ch.streamUrl, ingestionKey: ch.ingestionKey }; });
    setEditValues(init);
  }, [thirdPartyStreamingChannelsToEdit, broadcastingChannelName]);

  // Service YouTube présélectionné, une fois par demande (la page s'ouvre en haut, cf. Dashboard).
  const handledFocusYoutube = React.useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!focusYoutube || services.length === 0 || handledFocusYoutube.current === focusYoutube) return;
    handledFocusYoutube.current = focusYoutube;
    const yt = services.find(s => /youtube/i.test(s.name));
    if (yt) prefillForm(yt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusYoutube, services]);

  const prefillForm = (svc: ThirdPartyStreamingService) => {
    setNewServiceId(svc.id);
    setNewStreamUrl(svc.ingestionUrl ?? '');
    setNewName(`${svc.name} - ${broadcastingChannelName}`);
  };

  const handleToggleActive = async (index: number) => {
    const ch = channels[index];
    const r  = await switchThirdPartyStreamingChannelStatus({ ...ch, active: !ch.active });
    if (r.code === 1) {
      const updated = channels.map((c, i) => i === index ? { ...c, active: !c.active } : c);
      setChannels(updated);
      onChange?.(updated);
      showToast(ch.active ? 'Restream désactivé.' : 'Restream activé !', 'success');
    } else {
      showToast("Impossible de modifier l'état du restream.", 'error');
    }
  };

  const handleSave = async (index: number) => {
    const ch    = channels[index];
    const edits = editValues[index] ?? {};
    const updated: ThirdPartyStreamingChannel = {
      ...ch,
      name        : edits.name         ?? ch.name,
      streamUrl   : edits.streamUrl    ?? ch.streamUrl,
      ingestionKey: edits.ingestionKey ?? ch.ingestionKey,
    };
    setShowWaiting(true);
    const r = await patchChannel(updated);
    setShowWaiting(false);
    if (r.success) {
      const list = channels.map((c, i) => i === index ? updated : c);
      setChannels(list);
      onChange?.(list);
      setEditIndex(null);
      showToast(`Canal "${updated.name}" sauvegardé.`, 'success');
    } else {
      showToast(`Impossible de sauvegarder : ${r.error}`, 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (confirmDel === null) return;
    setShowWaiting(true);
    const r = await deleteThirdPartyStreamingChannel(confirmDel);
    setShowWaiting(false);
    setConfirmDel(null);
    if (r.code === 1) {
      const list = channels.filter(c => c.id !== confirmDel);
      setChannels(list);
      onChange?.(list);
      const ev: Record<number, Partial<ThirdPartyStreamingChannel>> = {};
      list.forEach((ch, i) => { ev[i] = { name: ch.name, streamUrl: ch.streamUrl, ingestionKey: ch.ingestionKey }; });
      setEditValues(ev);
      showToast('Restream supprimé.', 'success');
    } else {
      showToast('Impossible de supprimer le restream.', 'error');
    }
  };

  const handleAdd = async () => {
    if (broadcastingChannelId === 0) { showToast("Sauvegardez d'abord la chaîne.", 'error'); return; }
    if (!newName.trim())         { showToast('Le nom est obligatoire.', 'error'); return; }
    if (!newStreamUrl.trim())    { showToast("L'URL de stream est obligatoire.", 'error'); return; }
    if (!newIngestionKey.trim()) { showToast("La clé d'ingestion est obligatoire.", 'error'); return; }

    const capturedServiceId = newServiceId;
    setShowWaiting(true);
    const newCh: ThirdPartyStreamingChannel = {
      ...initializedThirdPartyStreamingChannel(),
      name                        : newName,
      streamUrl                   : newStreamUrl,
      ingestionKey                : newIngestionKey,
      broadcastingChannelId       : broadcastingChannelId,
      thirdPartyStreamingServiceId: capturedServiceId,
      active                      : false,
    };
    const r = await createThirdPartyStreamingChannel(newCh);
    setShowWaiting(false);
    if (r.code === 1) {
      const created: ThirdPartyStreamingChannel = {
        ...r.payload,
        thirdPartyStreamingServiceId: r.payload.thirdPartyStreamingServiceId ?? capturedServiceId,
      };
      const list     = [...channels, created];
      const newIndex = list.length - 1;
      setChannels(list);
      onChange?.(list);
      setEditValues(prev => ({
        ...prev,
        [newIndex]: { name: created.name, streamUrl: created.streamUrl, ingestionKey: created.ingestionKey },
      }));
      if (services.length) prefillForm(services[0]);
      setNewIngestionKey('');
      showToast('Restream ajouté !', 'success');
    } else {
      showToast('Impossible de créer le restream.', 'error');
    }
  };

  const connectedCount = channels.length;
  const activeCount    = channels.filter(c => c.active).length;
  const allOk          = connectedCount > 0 && activeCount > 0;

  const getServiceName = (id: number) =>
    services.find(s => s.id === id)?.name ?? `Service #${id}`;

  const getEv = (index: number, field: keyof ThirdPartyStreamingChannel, fallback: string): string => {
    const ev = editValues[index];
    if (ev && field in ev) return (ev[field] as string) ?? fallback;
    return fallback;
  };

  const configuredServiceIds = new Set(channels.map(c => c.thirdPartyStreamingServiceId));
  const popularServices      = services.slice(0, Math.max(0, 5 - channels.length));
  const notConnected         = popularServices.filter(s => !configuredServiceIds.has(s.id));
  const hasCustomSlot        = channels.length + notConnected.length < 5;

  return (
    <div className={styles.root}>

      {showWaiting && <div className={styles.overlay}><AnimatedWaitingIcon2/></div>}

      {toast && (
        <div className={classNames(styles.toast, toast.kind === 'success' ? styles.toastSuccess : styles.toastError)}>
          {toast.kind === 'success' ? <Ico.Check/> : '⚠'} {toast.msg}
        </div>
      )}

      {confirmDel !== null && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center' }}>
          <div style={{ background:'var(--color-Panel-niv1-BG)',borderRadius:12,padding:28,width:360,boxShadow:'0 20px 60px rgba(0,0,0,0.5)',display:'flex',flexDirection:'column',gap:16 }}>
            <p style={{ margin:0,fontWeight:600,color:'#e6edf3' }}>Supprimer ce restream&nbsp;?</p>
            <p style={{ margin:0,fontSize:'0.84rem',color:'#8b949e' }}>Cette action est irréversible.</p>
            <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
              <button className={styles.btnSecondary} onClick={() => setConfirmDel(null)}>Annuler</button>
              <button className={styles.btnDanger}    onClick={handleDeleteConfirm}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageTitleBlock}>
          <h1 className={styles.pageTitle}>Diffuser ailleurs</h1>
          <p className={styles.pageSubtitle}>Envoyez votre chaîne vers d'autres plateformes en quelques clics.</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnGuide}><Ico.Book/>Guide rapide</button>
          <button className={styles.btnIconOnly}><Ico.Dots/></button>
        </div>
      </div>

      {/* Destinations populaires */}
      <div className={styles.sectionLabel}>Destinations populaires</div>
      <div className={styles.platformGrid}>

        {channels.map((ch, index) => {
          const svcName = getServiceName(ch.thirdPartyStreamingServiceId);
          const vis     = getPlatformVisual(svcName);
          return (
            <div
              key       = {ch.id}
              className = {classNames(
                styles.platformCard,
                ch.active && styles.platformCardActive,
                !ch.active && ch.streamUrl && styles.platformCardReady,
              )}
            >
              <div className={styles.platformCardHead}>
                <div className={styles.platformIcon} style={{ background: vis.bg }}>
                  <PlatformLogo name={svcName} size={20}/>
                </div>
                <div className={styles.platformInfo}>
                  <p className={styles.platformName}>{svcName}</p>
                  {ch.active
                    ? <span className={classNames(styles.statusPill, styles.statusActive)}><span className={styles.statusDot}/>Actif</span>
                    : ch.streamUrl
                      ? <span className={classNames(styles.statusPill, styles.statusReady)}><span className={styles.statusDot}/>Prêt à diffuser</span>
                      : <span className={classNames(styles.statusPill, styles.statusOff)}><span className={styles.statusDot}/>Inactif</span>
                  }
                </div>
              </div>
              <p className={styles.platformDesc}>{ch.name || `Restream vers ${svcName}.`}</p>
              <div style={{ display:'flex', gap:6 }}>
                <button
                  className = {classNames(styles.platformBtn, styles.platformBtnEdit)}
                  onClick   = {() => setEditIndex(editIndex === index ? null : index)}
                >
                  <Ico.Edit/>Modifier
                </button>
                {!ch.active && (
                  <button
                    className = {classNames(styles.platformBtn, styles.platformBtnActivate)}
                    onClick   = {() => handleToggleActive(index)}
                  >
                    <Ico.Play/>Activer
                  </button>
                )}
                {ch.active && (
                  <button
                    className = {styles.platformBtn}
                    onClick   = {() => handleToggleActive(index)}
                    style     = {{ color: '#f87171', borderColor: 'rgba(248,113,113,0.3)' }}
                  >
                    Désactiver
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {notConnected.map(svc => {
          const vis = getPlatformVisual(svc.name);
          return (
            <div key={svc.id} className={styles.platformCard}>
              <div className={styles.platformCardHead}>
                <div className={styles.platformIcon} style={{ background: vis.bg }}>
                  <PlatformLogo name={svc.name} size={20}/>
                </div>
                <div className={styles.platformInfo}>
                  <p className={styles.platformName}>{svc.name}</p>
                  <span className={classNames(styles.statusPill, styles.statusOff)}><span className={styles.statusDot}/>Non connecté</span>
                </div>
              </div>
              <p className={styles.platformDesc}>Diffusez en direct sur votre chaîne {svc.name}.</p>
              <button
                className = {styles.platformBtn}
                onClick   = {() => {
                  prefillForm(svc);
                  document.getElementById('rs-add-form')?.scrollIntoView({ behavior:'smooth', block:'center' });
                }}
              >
                Connecter
              </button>
            </div>
          );
        })}

        {hasCustomSlot && (
          <div className={styles.platformCard}>
            <div className={styles.platformCardHead}>
              <div className={styles.platformIcon} style={{ background: '#3a7fe8' }}>
                <CustomStreamIcon size={20}/>
              </div>
              <div className={styles.platformInfo}>
                <p className={styles.platformName}>Flux personnalisé</p>
                <span className={classNames(styles.statusPill, styles.statusOff)}><span className={styles.statusDot}/>Non connecté</span>
              </div>
            </div>
            <p className={styles.platformDesc}>Diffusez vers un flux RTMP personnalisé.</p>
            <button
              className = {styles.platformBtn}
              onClick   = {() => { document.getElementById('rs-add-form')?.scrollIntoView({ behavior:'smooth', block:'center' }); }}
            >
              Connecter
            </button>
          </div>
        )}
      </div>

      {/* Inline edit panel */}
      {editIndex !== null && channels[editIndex] && (
        <div className={styles.editPanel}>
          <div className={styles.editPanelHead}>
            <span className={styles.editPanelTitle}>Modifier — {getServiceName(channels[editIndex].thirdPartyStreamingServiceId)}</span>
            <button className={styles.editPanelClose} onClick={() => setEditIndex(null)}>×</button>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Nom</label>
            <input
              className = {styles.formInput}
              type      = "text"
              value     = {getEv(editIndex, 'name', channels[editIndex].name)}
              onChange  = {e => setEditValues(prev => ({ ...prev, [editIndex]: { ...prev[editIndex], name: e.target.value } }))}
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>URL de diffusion (RTMP)</label>
            <input
              className   = {styles.formInput}
              type        = "text"
              placeholder = "rtmp://exemple.com/live"
              value       = {getEv(editIndex, 'streamUrl', channels[editIndex].streamUrl)}
              onChange    = {e => setEditValues(prev => ({ ...prev, [editIndex]: { ...prev[editIndex], streamUrl: e.target.value } }))}
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>Clé de stream</label>
            <div className={styles.formInputRow}>
              <div className={styles.formInputWrapper}>
                <input
                  className = {styles.formInput}
                  type      = {showEditKey[editIndex] ? 'text' : 'password'}
                  style     = {{ paddingRight: 36 }}
                  value     = {getEv(editIndex, 'ingestionKey', channels[editIndex].ingestionKey)}
                  onChange  = {e => setEditValues(prev => ({ ...prev, [editIndex]: { ...prev[editIndex], ingestionKey: e.target.value } }))}
                />
                <button
                  className = {styles.formInputIcon}
                  onClick   = {() => setShowEditKey(prev => ({ ...prev, [editIndex]: !prev[editIndex] }))}
                >
                  {showEditKey[editIndex] ? <Ico.EyeOff/> : <Ico.Eye/>}
                </button>
              </div>
              <button className={styles.formHelpBtn} title="Où trouver ma clé ?"><Ico.Help/></button>
            </div>
          </div>

          <div className={styles.editPanelActions}>
            <button className={styles.btnSecondary} onClick={() => setEditIndex(null)}>Annuler</button>
            <button className={styles.btnDanger}    onClick={() => { setConfirmDel(channels[editIndex].id); setEditIndex(null); }}>Supprimer</button>
            <button className={styles.btnPrimary}   onClick={() => handleSave(editIndex)}>Sauvegarder</button>
          </div>
        </div>
      )}

      {/* Main layout */}
      <div className={styles.mainLayout}>

        {/* Add-destination form */}
        <div className={styles.addCard} id="rs-add-form">
          <div className={styles.addCardHead}>
            <span className={styles.addCardHeadIcon}><Ico.Plus/></span>
            Ajouter une destination
          </div>

          <div className={styles.addCardBody}>
            <div className={styles.formRow}>
              <label className={styles.formLabel}>Service</label>
              <select
                className = {styles.formSelect}
                value     = {`${newServiceId}`}
                onChange  = {e => {
                  const id  = parseInt(e.target.value);
                  const svc = services.find(s => s.id === id);
                  if (svc) prefillForm(svc);
                }}
              >
                {services.map(s => (
                  <option key={s.id} value={`${s.id}`}>{s.name}</option>
                ))}
                <option value="-1">Flux personnalisé (RTMP)</option>
              </select>
            </div>

            <div className={styles.formRow}>
              <label className={styles.formLabel}>Nom</label>
              <input
                className   = {styles.formInput}
                type        = "text"
                placeholder = "Ex. : Ma chaîne secondaire"
                value       = {newName}
                onChange    = {e => setNewName(e.target.value)}
              />
            </div>

            <div className={styles.formRow}>
              <label className={styles.formLabel}>URL de diffusion (RTMP)</label>
              <input
                className   = {styles.formInput}
                type        = "text"
                placeholder = "rtmp://exemple.com/live"
                value       = {newStreamUrl}
                onChange    = {e => setNewStreamUrl(e.target.value)}
              />
            </div>

            <div className={styles.formRow}>
              <label className={styles.formLabel}>Clé de stream</label>
              <div className={styles.formInputRow}>
                <div className={styles.formInputWrapper}>
                  <input
                    className   = {styles.formInput}
                    type        = {showKey ? 'text' : 'password'}
                    placeholder = "••••••••••••••••"
                    style       = {{ paddingRight: 36 }}
                    value       = {newIngestionKey}
                    onChange    = {e => setNewIngestionKey(e.target.value)}
                  />
                  <button className={styles.formInputIcon} onClick={() => setShowKey(v => !v)}>
                    {showKey ? <Ico.EyeOff/> : <Ico.Eye/>}
                  </button>
                </div>
                <button className={styles.formHelpBtn} title="Où trouver ma clé ?"><Ico.Help/></button>
              </div>
            </div>

            <div className={styles.formNote}>
              <Ico.Lock/>
              Votre clé de stream est confidentielle et n'est jamais partagée.
            </div>
          </div>

          <div className={styles.addCardFooter}>
            <button className={styles.btnAdd} onClick={handleAdd} disabled={broadcastingChannelId === 0}>
              Ajouter la destination
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div className={styles.sidebar}>

          {/* Stats card */}
          <div className={styles.statsCard}>

            {/* Stat 1 — plateformes connectées */}
            <div className={styles.statItem}>
              <div className={styles.statIconWrap} style={{ background: 'rgba(43,86,63)', color: '#60a5fa' }}>
                <Ico.StatConnected/>
              </div>
              <div className={styles.statNum}>{connectedCount}</div>
              <div className={styles.statLabel}>Plateformes connectées</div>
              <div className={styles.statDesc}>
                Vous avez connecté {connectedCount} destination{connectedCount > 1 ? 's' : ''}.
              </div>
            </div>

            {/* Stat 2 — plateforme(s) active(s) */}
            <div className={styles.statItem}>
              <div className={styles.statIconWrap} style={{ background: 'rgba(31,59,117)', color: '#25c26e' }}>
                <Icon name="antenna" color="#FFFFFF"/>
              </div>
              <div className={styles.statNum}>{activeCount}</div>
              <div className={styles.statLabel}>Plateforme{activeCount > 1 ? 's' : ''} active{activeCount > 1 ? 's' : ''}</div>
              <div className={styles.statDesc}>Votre contenu est diffusé en ce moment.</div>
            </div>

            {/* Stat 3 — tout va bien / aucune diffusion */}
            <div className={styles.statItem}>
              {allOk ? (
                <>
                  {/* Cercle vert plein avec coche blanche centrée */}
                  <div className={styles.statIconWrap} style={{ background: '#25c26e', color: '#fff' }}>
                    <Ico.StatCheck/>
                  </div>
                  <div className={styles.statLabel} style={{ color: '#25c26e', marginTop: 8 }}>Tout va bien</div>
                  <div className={styles.statDesc}>
                    Votre chaîne peut aussi être diffusée sur {getServiceName(channels.find(c => c.active)?.thirdPartyStreamingServiceId ?? -1)}.
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.statIconWrap} style={{ background: 'rgba(107,114,128,0.15)', color: '#6b7280' }}>
                    <Ico.StatActive/>
                  </div>
                  <div className={styles.statLabel} style={{ marginTop: 8 }}>Aucune diffusion active</div>
                  <div className={styles.statDesc}>Activez une destination pour commencer.</div>
                </>
              )}
            </div>
          </div>

          {/* Guide card */}
          <div className={styles.guideCard}>
            {/*
              La fusée est positionnée en absolute en bas-gauche de la card,
              pour qu'elle déborde légèrement vers le bas comme dans la maquette.
              guideCard a position:relative et overflow:hidden.
            */}
            <div className={styles.guideRocket} aria-hidden="true">
              <img src="/images/rocket.png" alt=""/>
            </div>
            <div className={styles.guideBody}>
              <p className={styles.guideTitle}>Qu'est-ce que le restream&nbsp;?</p>
              <p className={styles.guideText}>
                Le restream vous permet d'envoyer votre direct vers plusieurs plateformes en même temps.
                Connectez vos destinations une fois, puis activez celles que vous souhaitez à chaque diffusion.
              </p>
              <button className={styles.guideLinkBtn}>
                <Ico.ExternalLink/>Voir le guide complet
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BroadcastingChannelCharacteristics_ThirdPartyStreamingSystemV2;